/* Built In Imports */
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box, Flex, Link } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import forEach from 'lodash/forEach';

/* Internal Imports */
/* Components */
import MidContainer from '@Layout/MidContainer';
import WrapLayout from '@Layout/WrapLayout';
import TopPubBanner from '@components/HeaderBanner/TopPubBanner';
import SectionTitle from '@components/Headings/SectionTitle';
import Breadcrum from '@components/Navigation/Breadcrum';
import SecNav from '@components/Navigation/SecNav';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import PubAlsoIn from '@components/UI/PubAlsoIn';
import CardSlider from '@components/UI/Publications/CardSlider';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import HeadComponent from '@components/Utility/HeadComponent';
import config from '@config';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Page Data
 */

const PubBookCat = ({ region, language, pageData }) => {
  const authContext = useContext(AuthContext);
  const route = useRouter();
  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  const sliceStr = str => {
    if (str) {
      const firstPart = str.slice(0, str.indexOf('[x]'));
      const lastPart = str.slice(str.indexOf(']') + 1, str.length);
      return [firstPart, lastPart];
    }
    return [];
  };

  const schemaData = {
    '@context': 'https://schema.org/',
    '@type': 'Book',
    name: pageData?.title || '',
    author: {
      '@type': 'Person',
      name: 'Sadhguru Jaggi Vasudev',
      description:
        'Sadhguru is a yogi, mystic, and visionary who has dedicated his life to the elevation of the physical, mental, and spiritual well-being of all people. He is the founder of the Isha Foundation, a non-profit organization dedicated to fostering global harmony and individual growth through yoga and meditation.',
      url: 'https://isha.sadhguru.org/',
      sameAs: [
        'https://www.facebook.com/sadhguru',
        'https://twitter.com/sadhguruJV',
        'https://www.instagram.com/sadhguru/',
        'https://www.youtube.com/user/sadhguru',
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'Isha Foundation',
    },
    isbn: '',
    numberOfPages: '',
    datePublished: pageData?.createdAt || '',
    inLanguage: language,
    description: pageData?.summary || '',
    image: pageData?.heroImage?.url || '',
    url: `https://isha.sadhguru.org${route.asPath}`,

    workExample: {
      '@type': 'Book',
      datePublished: pageData?.createdAt || '',
      isbn: '',
    },
  };

  const buyVis =
    (pageData?.placeholderText?.json?.amazonImageLink &&
      pageData?.amazonLink) ||
    (pageData?.placeholderText?.json?.flipkartImageLink &&
      pageData?.flipkartLink) ||
    (pageData?.placeholderText?.json?.ishalifeImageLink &&
      pageData.ishaLifeLink);

  return (
    <WrapLayout>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
      >
        {' '}
      </HeadComponent>
      <Breadcrum
        page={pageData.breadcrumbs}
        alsoin={pageData.alsoin}
        secNav={true}
        url=""
        region={region}
        lang={language}
        pageConfig={pageData?.pageConfig}
      />
      {pageData.alsoin?.length > 0 && (
        <SecNav
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={language}
          pageConfig={pageData?.pageConfig}
        />
      )}
      <Box
        as="section"
        className="page-wrap container-article"
        maxW="1300px !important"
        mx="auto"
        p="0 !important"
        w="100%"
        overflow="hidden"
      >
        <TopPubBanner
          sectionContent={{
            heroImage: pageData?.heroImage,
            title: pageData?.title,
          }}
          region={region}
          lang={language}
        />

        {render(pageData?.aboutTheBookDescription) &&
          render(pageData?.aboutTheBookDescription) !== '<p></p>' && (
            <Box m={{ base: '0', md: '40px' }}>
              {pageData?.isThisABook && (
                <SectionTitle
                  mt={{ base: '0', md: 'initial' }}
                  px={{ base: '2%', md: '0' }}
                  titleObj={{
                    title: pageData?.placeholderText?.json?.aboutTheBook,
                    color: '#02026C',
                    titleDecoration: 'idy-subtext',
                  }}
                />
              )}

              {!pageData?.isThisABook && (
                <SectionTitle
                  px={{ base: '2%', md: '0' }}
                  titleObj={{
                    title: pageData?.placeholderText?.json?.description,
                    color: '#02026C',
                    titleDecoration: 'idy-subtext',
                  }}
                />
              )}

              <Flex
                justify="center"
                className="event-text"
                maxW="690px"
                m="0 auto"
              >
                <StructuredTextParser
                  str={render(pageData?.aboutTheBookDescription)}
                  region={region}
                  lang={language}
                  px={{ base: '17px', md: 'initial' }}
                />
              </Flex>
            </Box>
          )}

        {render(pageData?.excerpt) &&
          render(pageData?.excerpt) !== '<p></p>' && (
            <Box my="40px" maxW="690px" m="0 auto">
              <SectionTitle
                px={{ base: '2%', md: '0' }}
                titleObj={{
                  title: pageData?.placeholderText?.json?.excerpt,
                  color: '#02026C',
                  titleDecoration: 'idy-subtext',
                }}
              />
              <Flex justify="center" className="event-text" maxW="690px">
                <StructuredTextParser
                  str={render(pageData?.excerpt)}
                  region={region}
                  lang={language}
                  px={{ base: '17px', md: 'initial' }}
                />
              </Flex>
            </Box>
          )}

        {pageData?.alsoin?.length > 0 && (
          <Box my="20px">
            <MidContainer w={'499px'}>
              <PubAlsoIn
                sectionContent={{
                  text: `<p>${
                    pageData?.placeholderText?.json?.booksAvailableText
                  } ${getAlsoIn(pageData?.alsoin)}</p>`,
                  cType: 'html',
                }}
                region={region}
                lang={language}
              />
            </MidContainer>
          </Box>
        )}

        {(pageData?.theNewyorkTimesBestseller ||
          pageData?.numberOfCopiesSold) && (
          <Flex
            align="center"
            justifyContent="center"
            flexDir={{ base: 'column', md: 'row' }}
            my="4rem"
            gridGap="2rem"
            justify="center"
          >
            {pageData?.theNewyorkTimesBestseller && (
              <Flex
                flexDir="column"
                align="center"
                textAlign="center"
                fontWeight={500}
                fontSize="16px"
                lineHeight="21.76px"
                fontFamily="'FedraSansStd-medium', sans-serif"
              >
                <LazyLoadImageComponent
                  src={`${config.imgPath}/d/46272/1690189732-newyorktimes.png`}
                  alt="newYorkTimes logo"
                  maxWidth={{ base: 190, md: 270 }}
                />
                <Box>Best Seller</Box>
              </Flex>
            )}

            {pageData?.numberOfCopiesSold && (
              <Flex
                align="center"
                justify="center"
                width="163px"
                height="163px"
                borderRadius="50%"
                bg="rgba(245, 240, 229, 1)"
              >
                <Box
                  textAlign="center"
                  fontSize="16px"
                  w="116px"
                  // h="90px"
                  fontFeatureSettings="'case' on"
                  letterSpacing="0.5px"
                  fontFamily="'FedraSansStd-medium', sans-serif"
                  lineHeight="21.76px"
                  fontWeight="400"
                >
                  {
                    sliceStr(
                      pageData?.placeholderText?.json?.numberOfCopiesSold
                    )[0]
                  }
                  <Box
                    fontSize="20px"
                    color="#CF4520"
                    fontFamily="FedraSansStd-medium"
                    fontFeatureSettings="'case' on"
                    fontStyle="normal"
                    fontWeight="500"
                    lineHeight="136%"
                  >
                    {pageData?.numberOfCopiesSold}
                  </Box>
                  {
                    sliceStr(
                      pageData?.placeholderText?.json?.numberOfCopiesSold
                    )[1]
                  }
                </Box>
              </Flex>
            )}
          </Flex>
        )}
        {(pageData?.theNewyorkTimesBestseller ||
          pageData?.numberOfCopiesSold) && (
          <Flex
            textAlign="center"
            verticalAlign="middle"
            m={0}
            p="0"
            justifyContent="center"
          >
            <LazyLoadImageComponent
              alt="divider"
              loading="lazy"
              m={0}
              p="0"
              title="divider"
              minHeight="20px"
              src={`${config.staticPath}/d/46272/1654103596-divider-yoga.svg`}
            />
          </Flex>
        )}
        {buyVis && (
          <Flex
            mt={{ base: '40px', md: '25px' }}
            mb={{ base: '60px', md: '80px' }}
            flexDir="column"
            align="center"
          >
            <SectionTitle
              px={{ base: '2%', md: '0' }}
              titleObj={{
                title: '',
                color: '#02026C',
                titleDecoration: 'flowers',
              }}
            />
            <Box
              fontSize="18px"
              color="rgba(207, 69, 32, 1)"
              mt="5px"
              fontFamily="'FedraSansStd-medium', sans-serif"
            >
              {pageData?.buyNowTitle}
            </Box>
            <Flex
              mt="2rem"
              mb="2rem"
              flexWrap="wrap"
              flexDir={{ base: 'column', md: 'row' }}
              gridGap={{ base: '20px', md: '1rem' }}
            >
              {pageData?.amazonLink && (
                <Link href={pageData?.amazonLink} target="_blank">
                  <LazyLoadImageComponent
                    alt="amazonImg"
                    w={{ base: '199px', md: '140.556px' }}
                    // h='44px'
                    objectFit="fill"
                    src={pageData?.placeholderText?.json?.amazonImageLink}
                  />
                </Link>
              )}
              {pageData?.flipkartLink && (
                <Link href={pageData?.flipkartLink} target="_blank">
                  <LazyLoadImageComponent
                    w={{ base: '199px', md: '140.556px' }}
                    // h='44px'
                    alt="flipkartImg"
                    objectFit="fill"
                    src={pageData?.placeholderText?.json?.flipkartImageLink}
                  />
                </Link>
              )}
              {pageData?.ishaLifeLink && (
                <Link href={pageData?.ishaLifeLink} target="_blank">
                  <LazyLoadImageComponent
                    w={{ base: '199px', md: '140.556px' }}
                    // h='44px'

                    alt="ishaImg"
                    objectFit="fill"
                    src={pageData?.placeholderText?.json?.ishalifeImageLink}
                  />
                </Link>
              )}
            </Flex>
          </Flex>
        )}

        <Box mb="0px">
          {pageData?.related_content?.length > 0 && (
            <SectionTitle
              px={{ base: '2%', md: '0' }}
              titleObj={{
                title: pageData?.placeholderText?.json?.moreLikeThis,
                color: '#02026C',
                titleDecoration: 'idy-subtext',
              }}
            />
          )}
          <CardSlider
            sectionContent={pageData?.related_content}
            region={region}
            lang={language}
          />
        </Box>
      </Box>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </WrapLayout>
  );
};

const getAlsoIn = alsoIn => {
  let langList = '';
  forEach(alsoIn, function (lang, index) {
    if (lang.title) {
      langList = `${langList}${langList ? ',' : ''} <a href='${lang.url}'>${
        lang.title
      }</a>`;
    }
  });
  return langList;
};

export const getStaticPaths = async () => {
  const publicationsLandingPaths = config.USE_LOCAL_URL
    ? [
        '/in/en/publications/a-taste-of-well-being-sadhguru-s-insight-s-for-your-gastronomics',
        '/in/en/publications/a-guru-always-takes-you-for-a-ride',
        `/in/en/publications/in-the-presence-of-the-master-combo-vol-2`,
        '/in/en/publications/adiyogi-the-source-of-yoga',
        `/in/en/publications/inner-engineering-a-yogis-guide-to-joy`,
        `/in/bn/publications/inner-engineering-a-yogis-guide-to-joy`,
        `/in/kn/publications/aaseyee-annt`,
        `/in/ta/publications/kmpnnnil-yook-rkciyngkll`,
      ]
    : await getLandingPaths(
        'L1',
        '/publications/',
        ['/publications/categories', '/publications/why-books'],
        'publication'
      );
  return {
    paths: publicationsLandingPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language, book },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    region,
    language,
    `publications/${book}`,
    { preview, data: previewData }
  );

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
    },
  };
};

export default PubBookCat;
